/**
 * lib/utilityIntel.js — Iter 37 Utility Intelligence V1.
 *
 * Pure helpers + restrained palette + label normalization. Drawing-First:
 * crossings render as small diamond markers with a thin connector line,
 * NOT loud blobs. Hotspots render as a soft ring tint — present but
 * non-dominant.
 */
import { api } from "@/lib/api";


// Restrained operational palette. NEVER use solid red — directive forbids
// "engineering certainty" vocabulary or color. Soft amber ("watch") and
// restrained orange ("review") are the two valid severities.
export const SEVERITY_TONE = {
  watch:  { color: "#f59e0b", soft: "rgba(245,158,11,0.18)", label: "Watch" },
  review: { color: "#e85d1f", soft: "rgba(232, 93, 31,0.20)", label: "Review" },
};


// Layer-type → human label (mirrors backend services/utility_intelligence.py).
export const LAYER_LABEL = {
  storm: "Storm Drainage",
  sanitary: "Sanitary Sewer",
  water: "Water",
  electrical: "Electrical",
  grading: "Grading",
  paving: "Paving",
  work_limits: "Work Limits",
  force_main: "Force Main",
};


export async function fetchUtilityIntel(sheetId, { signal } = {}) {
  if (!sheetId) return { crossings: [], hotspots: [], pipe_runs: [], line_crossings: [] };
  try {
    const { data } = await api.get(`/sheets/${sheetId}/utility_conflicts`, { signal });
    // Iter 45 — V2 fields (`pipe_runs`, `line_crossings`) are additive;
    // ensure they exist on the result so consumers can assume the shape.
    return {
      crossings: [],
      hotspots: [],
      pipe_runs: [],
      line_crossings: [],
      ...(data || {}),
    };
  } catch (e) {
    if (e?.name === "CanceledError") return null;
    return { crossings: [], hotspots: [], pipe_runs: [], line_crossings: [] };
  }
}


// Persistence — match the existing layer-visibility convention so the
// super's last toggle survives reload.
const LS_KEY = "fop_utility_intel_v1";
export function readUtilityIntelVisible() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === null) return true; // default ON per Iter 37 directive ("10s test")
    return v === "1";
  } catch {
    return true;
  }
}
export function writeUtilityIntelVisible(on) {
  try { localStorage.setItem(LS_KEY, on ? "1" : "0"); } catch {/* ignore */}
}
